import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api, { formatTL, formatDate } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { ArrowLeft, ArrowUpRight, ArrowDownRight, DoorClosed, StickyNote, X } from "lucide-react";

export default function ShiftDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { isAdmin } = useAuth();
  const [d, setD] = useState(null);
  const [movOpen, setMovOpen] = useState(null); // 'in' | 'out'
  const [forceOpen, setForceOpen] = useState(false);
  const [noteOpen, setNoteOpen] = useState(false);
  const load = () => api.get(`/shifts/${id}`).then((r) => setD(r.data));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);
  if (!d) return <div className="text-stone-500">Yükleniyor…</div>;
  return (
    <div data-testid="shift-detail-root" className="max-w-5xl">
      <button onClick={() => nav(-1)} className="inline-flex items-center gap-1 text-sm text-stone-500 hover:text-stone-800 mb-3"><ArrowLeft className="w-4 h-4" /> Geri</button>
      <div className="flex items-start justify-between mb-6 flex-wrap gap-3">
        <div>
          <div className="font-mono-code text-[10px] tracking-[0.3em] text-stone-500">VARDİYA DETAY</div>
          <h1 className="font-serif-display text-3xl">{d.shift_number}</h1>
          <div className="text-sm text-stone-500 mt-1 font-mono-code">
            {d.cashier_name} · {formatDate(d.opened_at)} → {d.closed_at ? formatDate(d.closed_at) : "açık"}
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          {isAdmin && d.status === "OPEN" && (
            <>
              <button data-testid="add-cash-in" onClick={() => setMovOpen("in")} className="inline-flex items-center gap-1 px-3 py-2 rounded-lg border border-emerald-300 text-emerald-800 text-xs hover:bg-emerald-50"><ArrowUpRight className="w-3.5 h-3.5" /> Nakit Giriş</button>
              <button data-testid="add-cash-out" onClick={() => setMovOpen("out")} className="inline-flex items-center gap-1 px-3 py-2 rounded-lg border border-red-300 text-red-700 text-xs hover:bg-red-50"><ArrowDownRight className="w-3.5 h-3.5" /> Nakit Çıkış</button>
              <button data-testid="force-close" onClick={() => setForceOpen(true)} className="inline-flex items-center gap-1 px-3 py-2 rounded-lg border border-stone-300 text-stone-800 text-xs hover:bg-stone-50"><DoorClosed className="w-3.5 h-3.5" /> Yönetici Kapatma</button>
            </>
          )}
          {isAdmin && (
            <button data-testid="add-manager-note" onClick={() => setNoteOpen(true)} className="inline-flex items-center gap-1 px-3 py-2 rounded-lg border border-amber-300 text-amber-800 text-xs hover:bg-amber-50"><StickyNote className="w-3.5 h-3.5" /> Yönetici Notu</button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <KPI label="Satış" v={d.sales_count} />
        <KPI label="Adet" v={d.units_sold} />
        <KPI label="Net Satış" v={formatTL(d.net)} />
        <KPI label="İndirim" v={formatTL(d.discount_total)} />
        <KPI label="Nakit Satış" v={formatTL(d.cash_sales)} />
        <KPI label="Havale Satış" v={formatTL(d.transfer_sales)} />
        <KPI label="Nakit İade" v={formatTL(d.refund_cash)} />
        <KPI label="Havale İade" v={formatTL(d.refund_transfer)} />
        <KPI label="Kasa Giriş" v={formatTL(d.cash_in)} />
        <KPI label="Kasa Çıkış" v={formatTL(d.cash_out)} />
        <KPI label="Açılış Nakit" v={formatTL(d.opening_cash)} />
        <KPI label="Beklenen" v={formatTL(d.expected_cash)} tone="amber" />
      </div>

      {d.status === "CLOSED" && (
        <div className="card-lux p-5 mb-4">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <KPI label="Sayılan" v={formatTL(d.closing_cash_counted)} />
            <KPI label="Fark" v={d.cash_difference === 0 ? "0,00 ₺" : (d.cash_difference > 0 ? "+" : "") + formatTL(d.cash_difference)} tone={d.cash_difference === 0 ? "stone" : d.cash_difference < 0 ? "red" : "emerald"} />
            <KPI label="Durum" v={d.force_closed ? "Yönetici Kapatma" : "Kasiyer Kapatma"} />
          </div>
          {d.cashier_difference_note && <div className="mt-3 text-sm"><span className="text-stone-500">Kasiyer açıklaması: </span>{d.cashier_difference_note}</div>}
          {d.manager_note && <div className="mt-1 text-sm"><span className="text-stone-500">Yönetici notu: </span>{d.manager_note}</div>}
          {d.close_reason && <div className="mt-1 text-sm"><span className="text-stone-500">Kapanış nedeni: </span>{d.close_reason}</div>}
        </div>
      )}

      <Section title="Satışlar">
        {d.sales.length === 0 ? <Empty text="Vardiyaya bağlı satış yok" /> : (
          <ul className="divide-y divide-stone-100">
            {d.sales.map((s) => (
              <li key={s.id} className="flex items-center gap-3 py-2">
                <Link to={`/satislar/${s.id}`} className="font-mono-code text-sm text-amber-800 hover:underline">{s.sale_number}</Link>
                <span className="text-xs text-stone-500 font-mono-code">{formatDate(s.created_at)}</span>
                <span className="text-xs text-stone-500 ml-auto">{s.payment_method?.toUpperCase()}</span>
                <span className="font-mono-code text-sm">{formatTL(s.total)}</span>
                <span className={`text-[10px] px-2 py-0.5 rounded-full border ${s.status === "cancelled" ? "bg-red-100 text-red-800 border-red-200" : s.status === "completed" ? "bg-emerald-100 text-emerald-800 border-emerald-200" : "bg-amber-100 text-amber-800 border-amber-200"}`}>{s.status}</span>
              </li>
            ))}
          </ul>
        )}
      </Section>

      <Section title="Kasa Hareketleri">
        {d.cash_movements.length === 0 ? <Empty text="Kasa hareketi yok" /> : (
          <ul className="divide-y divide-stone-100">
            {d.cash_movements.map((m) => (
              <li key={m.id} className="flex items-center gap-3 py-2">
                <span className={`text-[10px] px-2 py-0.5 rounded-full border ${m.type === "in" ? "bg-emerald-100 text-emerald-800 border-emerald-200" : "bg-red-100 text-red-800 border-red-200"}`}>{m.type === "in" ? "Giriş" : "Çıkış"}</span>
                <span className="text-sm">{m.note}</span>
                <span className="text-xs text-stone-500 ml-auto">{m.user_name}</span>
                <span className="font-mono-code text-sm">{m.type === "in" ? "+" : "-"}{formatTL(m.amount)}</span>
              </li>
            ))}
          </ul>
        )}
      </Section>

      {movOpen && (
        <CashMovementModal shiftId={id} type={movOpen} onClose={() => setMovOpen(null)} onDone={() => { setMovOpen(null); load(); }} />
      )}
      {forceOpen && (
        <ForceCloseModal shiftId={id} expected={d.expected_cash} onClose={() => setForceOpen(false)} onDone={() => { setForceOpen(false); load(); }} />
      )}
      {noteOpen && (
        <ManagerNoteModal shiftId={id} existing={d.manager_note || ""} onClose={() => setNoteOpen(false)} onDone={() => { setNoteOpen(false); load(); }} />
      )}
    </div>
  );
}

function KPI({ label, v, tone = "stone" }) {
  const t = { stone: "text-stone-900", amber: "text-amber-800", red: "text-red-700", emerald: "text-emerald-700" }[tone];
  return (
    <div className="card-lux p-3">
      <div className="text-[10px] uppercase tracking-widest text-stone-500">{label}</div>
      <div className={`mt-1 font-mono-code text-sm font-semibold ${t}`}>{v}</div>
    </div>
  );
}
function Section({ title, children }) {
  return (
    <div className="card-lux p-5 mb-4">
      <div className="font-serif-display text-xl mb-3">{title}</div>
      {children}
    </div>
  );
}
function Empty({ text }) { return <div className="text-sm text-stone-500 text-center py-4">{text}</div>; }

function CashMovementModal({ shiftId, type, onClose, onDone }) {
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    if (!(Number(amount) > 0)) return toast.error("Tutar sıfırdan büyük olmalı");
    if (!note.trim()) return toast.error("Açıklama zorunlu");
    setSaving(true);
    try {
      await api.post(`/shifts/${shiftId}/cash-movement`, { type, amount: Number(amount), note });
      toast.success("Kasa hareketi eklendi");
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "İşlem başarısız"); } finally { setSaving(false); }
  };
  return (
    <Modal title={type === "in" ? "Kasa Nakit Giriş" : "Kasa Nakit Çıkış"} onClose={onClose}>
      <label className="block mb-3">
        <div className="text-[10px] uppercase text-stone-500 tracking-widest mb-1 font-semibold">Tutar (₺)</div>
        <input data-testid="mov-amount" type="number" min="0" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} className="w-full px-3 py-2 border border-stone-200 rounded-lg font-mono-code" autoFocus />
      </label>
      <label className="block mb-3">
        <div className="text-[10px] uppercase text-stone-500 tracking-widest mb-1 font-semibold">Açıklama</div>
        <input data-testid="mov-note" value={note} onChange={(e) => setNote(e.target.value)} className="w-full px-3 py-2 border border-stone-200 rounded-lg" placeholder="Ör: Owner kasadan nakit aldı" />
      </label>
      <div className="flex justify-end gap-2">
        <button onClick={onClose} className="px-4 py-2 rounded-lg border border-stone-200 text-sm">Vazgeç</button>
        <button data-testid="mov-submit" onClick={submit} disabled={saving} className="px-4 py-2 rounded-lg gold-gradient text-white text-sm disabled:opacity-60">{saving ? "Kaydediliyor…" : "Kaydet"}</button>
      </div>
    </Modal>
  );
}
function ForceCloseModal({ shiftId, expected, onClose, onDone }) {
  const [counted, setCounted] = useState("");
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    if (counted === "") return toast.error("Sayılan nakit giriniz");
    if (!reason.trim()) return toast.error("Kapatma nedeni zorunlu");
    setSaving(true);
    try {
      await api.post(`/shifts/${shiftId}/force-close`, { counted_cash: Number(counted), reason });
      toast.success("Vardiya yönetici olarak kapatıldı");
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "İşlem başarısız"); } finally { setSaving(false); }
  };
  return (
    <Modal title="Vardiyayı Yönetici Olarak Kapat" onClose={onClose}>
      <p className="text-sm text-stone-500 mb-3">Beklenen kasa: <span className="font-mono-code">{formatTL(expected)}</span></p>
      <label className="block mb-3">
        <div className="text-[10px] uppercase text-stone-500 tracking-widest mb-1 font-semibold">Sayılan Nakit (₺)</div>
        <input data-testid="force-counted" type="number" min="0" step="0.01" value={counted} onChange={(e) => setCounted(e.target.value)} className="w-full px-3 py-2 border border-stone-200 rounded-lg font-mono-code" />
      </label>
      <label className="block mb-3">
        <div className="text-[10px] uppercase text-stone-500 tracking-widest mb-1 font-semibold">Neden</div>
        <textarea data-testid="force-reason" rows={2} value={reason} onChange={(e) => setReason(e.target.value)} className="w-full px-3 py-2 border border-stone-200 rounded-lg" />
      </label>
      <div className="flex justify-end gap-2">
        <button onClick={onClose} className="px-4 py-2 rounded-lg border border-stone-200 text-sm">Vazgeç</button>
        <button data-testid="force-submit" onClick={submit} disabled={saving} className="px-4 py-2 rounded-lg bg-red-600 text-white text-sm disabled:opacity-60">{saving ? "Kapatılıyor…" : "Zorunlu Kapat"}</button>
      </div>
    </Modal>
  );
}
function ManagerNoteModal({ shiftId, existing, onClose, onDone }) {
  const [note, setNote] = useState(existing);
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    if (!note.trim()) return toast.error("Not boş olamaz");
    setSaving(true);
    try {
      await api.post(`/shifts/${shiftId}/manager-note`, { note });
      toast.success("Not kaydedildi");
      onDone();
    } catch (e) { toast.error("İşlem başarısız"); } finally { setSaving(false); }
  };
  return (
    <Modal title="Yönetici Notu" onClose={onClose}>
      <textarea data-testid="mgr-note" rows={3} value={note} onChange={(e) => setNote(e.target.value)} className="w-full px-3 py-2 border border-stone-200 rounded-lg mb-3" placeholder="Not…" />
      <div className="flex justify-end gap-2">
        <button onClick={onClose} className="px-4 py-2 rounded-lg border border-stone-200 text-sm">Vazgeç</button>
        <button data-testid="mgr-note-save" onClick={submit} disabled={saving} className="px-4 py-2 rounded-lg gold-gradient text-white text-sm disabled:opacity-60">{saving ? "Kaydediliyor…" : "Kaydet"}</button>
      </div>
    </Modal>
  );
}
function Modal({ title, onClose, children }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl w-full max-w-md border border-stone-200 p-5">
        <div className="flex items-center justify-between mb-3">
          <div className="font-serif-display text-xl">{title}</div>
          <button onClick={onClose} className="p-1 rounded-md hover:bg-stone-100"><X className="w-4 h-4" /></button>
        </div>
        {children}
      </div>
    </div>
  );
}
